import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, CartesianGrid } from "recharts";
import { ChartLineUp, CurrencyDollar, Percent, Package, CircleNotch, Trophy, Buildings } from "@phosphor-icons/react";
import { useTranslation } from "react-i18next";

const PIE_COLORS = ["#8a8a8a", "#D4AF37", "#3b82f6", "#10b981"];

function Money({ v }) { return <>${(v ?? 0).toLocaleString("es", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</>; }

export default function Analytics() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [a, setA] = useState(null);
  const [adm, setAdm] = useState(null);
  const isAdmin = user?.role === "admin";
  const STATUS_LABEL = { generado: t("analytics.status.generado"), pedido: t("analytics.status.pedido"), en_produccion: t("analytics.status.en_produccion"), completado: t("analytics.status.completado") };

  useEffect(() => {
    api.get("/analytics").then((r) => setA(r.data)).catch(() => setA(false));
    if (user?.role === "admin") api.get("/admin/analytics").then((r) => setAdm(r.data)).catch(() => {});
  }, [user]);

  if (a === null) return <div className="p-8"><CircleNotch className="animate-spin text-gold" size={28} /></div>;
  if (a === false) return <div className="p-8 text-neutral-400">{t("analytics.loadError")}</div>;

  const pieData = Object.entries(a.status_counts || {}).map(([k, v]) => ({ name: STATUS_LABEL[k] || k, value: v, key: k }));

  const cards = [
    { icon: Package, l: t("analytics.designsTotal"), v: a.designs_total, gold: false },
    { icon: ChartLineUp, l: t("analytics.ordersTotal"), v: a.orders_total, gold: false },
    { icon: CurrencyDollar, l: t("analytics.revenue"), v: <Money v={a.revenue} />, gold: false },
    { icon: Percent, l: t("analytics.commission"), v: <Money v={a.commission} />, gold: true },
  ];

  return (
    <div className="p-8 max-w-6xl">
      <h1 className="text-3xl font-bold tracking-tight">{t("analytics.title")}</h1>
      <p className="text-neutral-400 mt-1 mb-8">{t("analytics.subtitle")}</p>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8" data-testid="analytics-cards">
        {cards.map((c, i) => (
          <div key={i} className={`border rounded-xl p-5 ${c.gold ? "border-gold/30 bg-[#151208]" : "border-white/10 bg-[#121212]"}`}>
            <c.icon size={20} className="text-gold mb-3" weight="duotone" />
            <div className={`text-2xl font-bold ${c.gold ? "text-gold" : ""}`}>{c.v}</div>
            <div className="text-sm text-neutral-400">{c.l}</div>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-6 mb-6">
        {/* Monthly */}
        <div className="border border-white/10 rounded-xl p-5 bg-[#121212]">
          <h3 className="font-semibold mb-4">{t("analytics.monthly")}</h3>
          {a.monthly?.length ? (
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={a.monthly}>
                <CartesianGrid strokeDasharray="3 3" stroke="#ffffff10" vertical={false} />
                <XAxis dataKey="month" stroke="#888" fontSize={12} />
                <YAxis stroke="#888" fontSize={12} />
                <Tooltip contentStyle={{ background: "#1a1a1a", border: "1px solid #ffffff20", borderRadius: 8 }} />
                <Bar dataKey="orders" name={t("analytics.ordersTotal")} fill="#D4AF37" radius={[4, 4, 0, 0]} />
                <Bar dataKey="revenue" name={t("analytics.revenueBar")} fill="#3b82f6" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : <p className="text-sm text-neutral-500 py-10 text-center">{t("analytics.noData")}</p>}
        </div>

        {/* Status pie */}
        <div className="border border-white/10 rounded-xl p-5 bg-[#121212]">
          <h3 className="font-semibold mb-4">{t("analytics.statusTitle")}</h3>
          {pieData.length ? (
            <ResponsiveContainer width="100%" height={240}>
              <PieChart>
                <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} label>
                  {pieData.map((e, i) => <Cell key={i} fill={PIE_COLORS[["generado", "pedido", "en_produccion", "completado"].indexOf(e.key)] || "#888"} />)}
                </Pie>
                <Tooltip contentStyle={{ background: "#1a1a1a", border: "1px solid #ffffff20", borderRadius: 8 }} />
              </PieChart>
            </ResponsiveContainer>
          ) : <p className="text-sm text-neutral-500 py-10 text-center">{t("analytics.noData")}</p>}
        </div>
      </div>

      {/* Top products */}
      <div className="border border-white/10 rounded-xl p-5 bg-[#121212] mb-6">
        <h3 className="font-semibold mb-4 flex items-center gap-2"><Trophy size={18} className="text-gold" /> {t("analytics.topProducts")}</h3>
        {a.top_products?.length ? (
          <div className="space-y-2">
            {a.top_products.map((p, i) => (
              <div key={i} className="flex items-center gap-3">
                <span className="w-6 text-neutral-500 font-mono text-sm">#{i + 1}</span>
                <span className="flex-1 truncate">{p.name}</span>
                <div className="w-40 bg-white/5 rounded-full h-2 overflow-hidden">
                  <div className="h-full bg-gold" style={{ width: `${(p.count / a.top_products[0].count) * 100}%` }} />
                </div>
                <span className="w-10 text-right text-sm text-neutral-400">{p.count}</span>
              </div>
            ))}
          </div>
        ) : <p className="text-sm text-neutral-500">{t("analytics.noDesigns")}</p>}
      </div>

      {/* Admin: platform-wide */}
      {isAdmin && adm && (
        <div className="border border-gold/30 rounded-xl p-5 bg-[#151208]" data-testid="admin-analytics">
          <h3 className="font-semibold mb-4 flex items-center gap-2 text-gold"><Buildings size={18} /> {t("analytics.platform")}</h3>
          <div className="grid grid-cols-3 gap-4 mb-5">
            <div><div className="text-xs text-neutral-400">{t("analytics.totalGmv")}</div><div className="text-2xl font-bold"><Money v={adm.total_gmv} /></div></div>
            <div><div className="text-xs text-gold">{t("analytics.totalCommission")}</div><div className="text-2xl font-bold text-gold"><Money v={adm.total_commission} /></div></div>
            <div><div className="text-xs text-neutral-400">{t("analytics.totalDesigns")}</div><div className="text-2xl font-bold">{adm.total_designs}</div></div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-neutral-400 text-left border-b border-white/10">
                <th className="py-2">{t("analytics.store")}</th><th>{t("analytics.designs")}</th><th>{t("analytics.revenue")}</th><th className="text-gold">{t("analytics.commission")}</th></tr></thead>
              <tbody>
                {adm.stores.map((s, i) => (
                  <tr key={i} className="border-b border-white/5">
                    <td className="py-2">{s.store}</td><td>{s.designs}</td><td><Money v={s.revenue} /></td>
                    <td className="text-gold"><Money v={s.commission} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
